import { z } from 'zod';
import { ModelProvider, ModelStatus, ModelType, ProviderHealthStatus } from '../enums/common';

// ========================================
// MODEL PARAMETER LIMITS
// ========================================

export const ModelParameterRangeSchema = z.object({
  min: z.number(),
  max: z.number(),
  default: z.number(),
});

export type ModelParameterRange = z.infer<typeof ModelParameterRangeSchema>;

export const ModelParametersSchema = z.object({
  temperature: ModelParameterRangeSchema,
  topP: ModelParameterRangeSchema,
  frequencyPenalty: ModelParameterRangeSchema,
  presencePenalty: ModelParameterRangeSchema,
});

export type ModelParameters = z.infer<typeof ModelParametersSchema>;

// ========================================
// MODEL PRICING
// ========================================

export const ModelPricingSchema = z.object({
  inputCostPer1kTokens: z.number().nonnegative(),
  outputCostPer1kTokens: z.number().nonnegative(),
  currency: z.string().length(3).default('USD'),
});

export type ModelPricing = z.infer<typeof ModelPricingSchema>;

// ========================================
// MODEL LIMITS
// ========================================

export const ModelLimitsSchema = z.object({
  maxTokens: z.number().int().nonnegative(),
  maxRequestsPerMin: z.number().int().nonnegative(),
  contextWindow: z.number().int().nonnegative(),
});

export type ModelLimits = z.infer<typeof ModelLimitsSchema>;

// ========================================
// MODEL USAGE STATS
// ========================================

export const ModelUsageStatsSchema = z.object({
  totalCalls: z.number().int().nonnegative(),
  totalTokens: z.number().int().nonnegative(),
  totalCost: z.number().nonnegative(),
  avgResponseTime: z.number().nonnegative(),
});

export type ModelUsageStats = z.infer<typeof ModelUsageStatsSchema>;

// ========================================
// MODEL CONFIGURATION (Database Entity)
// ========================================

export const ModelConfigurationSchema = z.object({
  id: z.string().uuid(),
  providerId: z.nativeEnum(ModelProvider),
  modelId: z.string().min(1),
  displayName: z.string().min(1),
  description: z.string().nullable().optional(),
  modelType: z.nativeEnum(ModelType).default(ModelType.CHAT),
  inputPricePerToken: z.number().nonnegative(),
  outputPricePerToken: z.number().nonnegative(),
  maxTokens: z.number().int().nonnegative().nullable().optional(),
  contextWindow: z.number().int().nonnegative().nullable().optional(),
  capabilities: z.array(z.string()),
  isEnabled: z.boolean().default(true),
  createdAt: z.union([z.date(), z.string()]),
  updatedAt: z.union([z.date(), z.string()]),
});

export type ModelConfiguration = z.infer<typeof ModelConfigurationSchema>;

// ========================================
// AI MODEL (Frontend-friendly format)
// ========================================

export const AiModelSchema = z.object({
  id: z.string(),
  name: z.string(),
  displayName: z.string(),
  provider: z.nativeEnum(ModelProvider),
  providerName: z.string(),
  status: z.nativeEnum(ModelStatus),
  type: z.nativeEnum(ModelType),
  capabilities: z.array(z.string()),
  pricing: ModelPricingSchema,
  limits: ModelLimitsSchema,
  parameters: ModelParametersSchema.optional(),
  usage: ModelUsageStatsSchema.optional(),
  addedDate: z.string(),
  lastUsed: z.string().nullable().optional(),
});

export type AiModel = z.infer<typeof AiModelSchema>;

// ========================================
// CREATE MODEL CONFIGURATION
// ========================================

export const CreateModelConfigurationSchema = z.object({
  providerId: z.nativeEnum(ModelProvider),
  modelId: z.string().min(1).max(100),
  displayName: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  modelType: z.nativeEnum(ModelType).default(ModelType.CHAT),
  inputPricePerToken: z.number().nonnegative(),
  outputPricePerToken: z.number().nonnegative(),
  maxTokens: z.number().int().nonnegative().optional(),
  contextWindow: z.number().int().nonnegative().optional(),
  capabilities: z.array(z.string()),
});

export type CreateModelConfiguration = z.infer<typeof CreateModelConfigurationSchema>;

// ========================================
// UPDATE MODEL CONFIGURATION
// ========================================

export const UpdateModelConfigurationSchema = z.object({
  displayName: z.string().min(1).max(200).optional(),
  description: z.string().max(2000).nullable().optional(),
  inputPricePerToken: z.number().nonnegative().optional(),
  outputPricePerToken: z.number().nonnegative().optional(),
  maxTokens: z.number().int().nonnegative().nullable().optional(),
  contextWindow: z.number().int().nonnegative().nullable().optional(),
  capabilities: z.array(z.string()).optional(),
  isEnabled: z.boolean().optional(),
});

export type UpdateModelConfiguration = z.infer<typeof UpdateModelConfigurationSchema>;

// ========================================
// CUSTOMER MODEL ACCESS
// ========================================

export const BusinessModelAccessSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  modelConfigId: z.string().uuid(),
  isEnabled: z.boolean().default(true),
  rateLimitRpm: z.number().int().nonnegative().nullable().optional(),
  rateLimitTpm: z.number().int().nonnegative().nullable().optional(),
  priorityScore: z.number().int().default(0),
  createdAt: z.union([z.date(), z.string()]),
  updatedAt: z.union([z.date(), z.string()]),
});

export type BusinessModelAccess = z.infer<typeof BusinessModelAccessSchema>;

export const UpdateBusinessModelAccessSchema = z.object({
  isEnabled: z.boolean().optional(),
  rateLimitRpm: z.number().int().nonnegative().nullable().optional(),
  rateLimitTpm: z.number().int().nonnegative().nullable().optional(),
  priorityScore: z.number().int().optional(),
});

export type UpdateBusinessModelAccess = z.infer<typeof UpdateBusinessModelAccessSchema>;

// ========================================
// MODEL STATS (Aggregated)
// ========================================

export const ModelStatsSchema = z.object({
  totalModels: z.number().int().nonnegative(),
  activeModels: z.number().int().nonnegative(),
  totalProviders: z.number().int().nonnegative(),
  totalCalls: z.number().int().nonnegative(),
  totalCost: z.number().nonnegative(),
  avgResponseTime: z.number().nonnegative(),
  mostUsedModel: z.string().nullable().optional(),
});

export type ModelStats = z.infer<typeof ModelStatsSchema>;

// ========================================
// PROVIDER
// ========================================

export const ProviderSchema = z.object({
  id: z.nativeEnum(ModelProvider),
  name: z.string(),
  displayName: z.string(),
  status: z.enum(['active', 'inactive', 'error']),
  apiKeyConfigured: z.boolean(),
  apiKeyLastFour: z.string().nullable().optional(),
  models: z.number().int().nonnegative(),
  activeModels: z.number().int().nonnegative(),
  totalCalls: z.number().int().nonnegative(),
  totalCost: z.number().nonnegative(),
  healthStatus: z.nativeEnum(ProviderHealthStatus),
  lastHealthCheck: z.string().nullable().optional(),
  supportedTypes: z.array(z.nativeEnum(ModelType)),
});

export type Provider = z.infer<typeof ProviderSchema>;

// ========================================
// PROVIDER TEST RESULT
// ========================================

export const ProviderTestResultSchema = z.object({
  success: z.boolean(),
  providerId: z.nativeEnum(ModelProvider),
  status: z.nativeEnum(ProviderHealthStatus),
  responseTime: z.number().nonnegative().optional(),
  message: z.string(),
  error: z.string().optional(),
});

export type ProviderTestResult = z.infer<typeof ProviderTestResultSchema>;

// ========================================
// QUERY SCHEMAS
// ========================================

/** Maximum number of items per page for pagination across the application */
export const MAX_PAGE_SIZE = 100;

export const ModelListQuerySchema = z.object({
  provider: z.nativeEnum(ModelProvider).optional(),
  status: z.nativeEnum(ModelStatus).optional(),
  type: z.nativeEnum(ModelType).optional(),
  isEnabled: z.coerce.boolean().optional(),
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).optional().default(50),
  offset: z.coerce.number().int().min(0).optional().default(0),
});

export type ModelListQuery = z.infer<typeof ModelListQuerySchema>;

export const BusinessAccessQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).optional().default(20),
  offset: z.coerce.number().int().min(0).optional().default(0),
});

export type BusinessAccessQuery = z.infer<typeof BusinessAccessQuerySchema>;

// ========================================
// RESPONSE SCHEMAS
// ========================================

export const ModelListResponseSchema = z.object({
  models: z.array(AiModelSchema),
  total: z.number().int().nonnegative(),
});

export type ModelListResponse = z.infer<typeof ModelListResponseSchema>;

export const ProviderListResponseSchema = z.object({
  providers: z.array(ProviderSchema),
  total: z.number().int().nonnegative(),
});

export type ProviderListResponse = z.infer<typeof ProviderListResponseSchema>;

export const BusinessModelAccessListResponseSchema = z.object({
  accessList: z.array(
    BusinessModelAccessSchema.extend({
      businessName: z.string().optional(),
    }),
  ),
  total: z.number().int().nonnegative(),
});

export type BusinessModelAccessListResponse = z.infer<typeof BusinessModelAccessListResponseSchema>;
